import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

const PREFIX = 'survale-customer-portal';

/**
 * Drop-in replacement for angular-2-local-storage's LocalStorageService.
 * Removed because angular-2-local-storage is a View Engine library incompatible
 * with Angular 16 (no ngcc to compile it). Uses native browser localStorage directly.
 *
 * This is the ONLY place in the app that should touch `window.localStorage` directly — every
 * other class (including the legacy AppCache singleton, bridged in via app-injector.ts since it
 * predates Angular DI) goes through here, so storage-quota handling lives in exactly one place.
 */
@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  constructor(private toaster: ToastrService) {}

  private prefixedKey(key: string): string {
    return `${PREFIX}.${key}`;
  }

  /**
   * Whether an error was caused by the browser's local storage being full. Browsers surface this
   * as a DOMException named 'QuotaExceededError' (code 22), or the older Firefox
   * 'NS_ERROR_DOM_QUOTA_REACHED' (code 1014).
   */
  private isQuotaExceededError(error: any): boolean {
    return !!error && (
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      error.code === 22 ||
      error.code === 1014
    );
  }

  /**
   * Shared failure handler for every write below — logs, and for the specific "storage is full"
   * case shows an actionable toast. Never throws, so a failed write never aborts whatever the
   * caller was doing (e.g. saving a dashboard) — callers just get `false`/`null` back and the
   * data simply doesn't get cached this time.
   */
  private handleStorageError(error: any, action: string): void {
    console.error(`LocalStorageService: failed to ${action}`, error);
    if (this.isQuotaExceededError(error)) {
      this.toaster.error('Your browser\'s local storage is full. Please clear your local storage and try again.');
    }
  }

  get<T = any>(key: string): T | null {
    try {
      const raw = localStorage.getItem(this.prefixedKey(key));
      return raw !== null ? JSON.parse(raw) : null;
    } catch (error) {
      this.handleStorageError(error, `read "${key}"`);
      return null;
    }
  }

  set(key: string, value: any): boolean {
    try {
      localStorage.setItem(this.prefixedKey(key), JSON.stringify(value));
      return true;
    } catch (error) {
      this.handleStorageError(error, `write "${key}"`);
      return false;
    }
  }

  remove(key: string): boolean {
    try {
      localStorage.removeItem(this.prefixedKey(key));
      return true;
    } catch (error) {
      this.handleStorageError(error, `remove "${key}"`);
      return false;
    }
  }

  keys(): string[] {
    const pfx = `${PREFIX}-`;
    return Object.keys(localStorage)
      .filter(k => k.startsWith(pfx))
      .map(k => k.slice(pfx.length));
  }

  clearAll(pattern?: string): boolean {
    try {
      const pfx = `${PREFIX}-`;
      const regex = pattern ? new RegExp(pattern) : null;
      Object.keys(localStorage)
        .filter(k => k.startsWith(pfx))
        .filter(k => !regex || regex.test(k.slice(pfx.length)))
        .forEach(k => localStorage.removeItem(k));
      return true;
    } catch (error) {
      this.handleStorageError(error, 'clear entries');
      return false;
    }
  }

  // --- Raw (unprefixed, unencoded) access -----------------------------------------------------
  // For legacy callers (AppCache) that need the exact key they pass and store plain strings
  // as-is, without this service's JSON encoding or key prefix. Shares the same quota handling.

  getRaw(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      this.handleStorageError(error, `read "${key}"`);
      return null;
    }
  }

  setRaw(key: string, value: string): boolean {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      this.handleStorageError(error, `write "${key}"`);
      return false;
    }
  }

  removeRaw(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      this.handleStorageError(error, `remove "${key}"`);
      return false;
    }
  }

  clearAllRaw(): boolean {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      this.handleStorageError(error, 'clear all entries');
      return false;
    }
  }
}
